/** Overflow (⋯) menu of monospace items, with a danger variant.
 *  Square, hairline-bordered, paper ground — no radius, no shadow. */

import React, { useEffect, useRef, useState } from 'react';
import { cn } from './cn';
import { TYPE } from './primitives';

export interface ActionMenuItem {
  label: string;
  onSelect: () => void;
  danger?: boolean;
}

export const ActionMenu: React.FC<{
  items: ActionMenuItem[];
  className?: string;
  'aria-label'?: string;
}> = ({ items, className, 'aria-label': ariaLabel = 'More actions' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  if (items.length === 0) return null;

  return (
    <div className={cn('relative', className)} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label={ariaLabel}
        aria-expanded={open}
        className={cn(
          'px-2 py-1 leading-none text-[16px] transition-colors duration-[120ms]',
          open ? 'text-accent' : 'text-ink-muted hover:text-ink'
        )}
      >
        ⋯
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 min-w-[180px] bg-surface border border-rule-base rounded-xl shadow-lift overflow-hidden">
          {items.map((item, i) => (
            <button
              key={i}
              type="button"
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
              className={cn(
                TYPE.meta,
                'block w-full text-left px-4 py-[10px] transition-colors duration-[120ms]',
                i > 0 && 'border-t border-rule',
                item.danger
                  ? 'text-bad hover:bg-bad-tint'
                  : 'text-ink-soft hover:bg-ground hover:text-ink'
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
